// Page docs (structure + permissions + flows): pages/articles/README.md
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import NavBar from "@/components/Importable/Navigation";
import SubscriptionBanner from "@/components/Banners/SubscriptionBanner";
import SectionFooter from "@/components/CreateForm/Footer";
import Sidenav from "@/components/Sidenav";
import { featuredRegulations } from "@/data/regulationsData";
import { getAllNewlyAdded, mergeNewlyAdded, getIndexCache, setIndexCache } from "@/utils/localStore";
import { purple } from "@/theme/colors";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useDataVersionCheck } from "@/hooks/useDataVersionCheck";
import { openAccessGateModal } from "@/utils/accessGate";
import { useGetArticlesQuery } from "@/src/services/api";
import { isLocalDev, apiArticleToCard } from "@/utils/apiMappers";
import { useProgressiveLoading } from "@/hooks/useProgressiveLoading";
import ArticleCardSkeleton from "@/components/Skeleton/ArticleCardSkeleton";
import { ArticleCard } from "@/components/Importable/Cards";
import { IndexPageHead, IndexLayoutShell, IndexListHeader, IndexResultsGrid, useRelevanceSplit, CreateDraftCard } from "@/components/Importable/articles-and-regulations";
import FilterBar from "@/components/OrganizationSections/FilterBar";
import { useIndexFiltering, INDEX_FILTER_MIN } from "@/hooks/useIndexFiltering";
import { useIndexSideNav } from "@/hooks/useIndexSideNav";
import { useIndexLayout } from "@/hooks/useIndexLayout";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const ACCENT = {
  primary: purple.primary,
  dark: purple.dark
};
const ensureTrimmedString = value => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str.length ? str : null;
};
const getArticleRouteSegment = article => {
  if (!article) return null;
  return ensureTrimmedString(article.slug) || ensureTrimmedString(article.id) || ensureTrimmedString(article.approvedId);
};
const getArticleNavKey = (article, index) => {
  return getArticleRouteSegment(article) ?? `article-${index}`;
};
const getArticleLabel = (article, index) => {
  const shortTitle = ensureTrimmedString(article.shortTitle);
  if (shortTitle) return shortTitle;
  const title = ensureTrimmedString(article.title);
  if (title) return title;
  return `Article ${index + 1}`;
};

/**
 * Data layer for the articles index: SSR-safe cache hydration, the live API
 * query (skipped while a fresh cache exists), and the local-dev fallback merge.
 * Kept page-local because its hydration ordering is entity-specific and
 * intentionally delicate (see inline notes).
 */
function useArticlesIndexData() {
  const cacheStale = useDataVersionCheck("articles");
  // Read once on mount via useEffect to avoid the SSR-safe race where
  // `getIndexCache` returns null before the localStore `hydrated` flag is set.
  const [cachedArticles, setCachedArticles] = useState(null);
  // Always start empty so the first render matches server HTML (avoids
  // hydration mismatch when in-memory cache survives HMR).
  const [publishedArticles, setPublishedArticles] = useState([]);

  // Hydrate from index cache after mount (SSR-safe). Retry on next macrotask
  // in case localStore's hydration timer hadn't fired yet.
  useEffect(() => {
    const apply = () => {
      const c = getIndexCache("articles");
      if (c?.length) {
        setCachedArticles(c);
        setPublishedArticles(prev => prev.length === 0 ? c : prev);
        return true;
      }
      return false;
    };
    if (apply()) return;
    const t = setTimeout(apply, 0);
    return () => clearTimeout(t);
  }, []);

  // Fetch live articles from API
  const {
    data: apiArticles,
    isError: articlesError
  } = useGetArticlesQuery(undefined, {
    skip: (cachedArticles?.length ?? 0) > 0 && !cacheStale
  });

  // Replace list with API data
  useEffect(() => {
    if (!apiArticles) return;
    const cards = apiArticles.filter(a => a.isActive && a.approvalStatus === "approved").map(art => {
      const c = apiArticleToCard(art);
      return {
        id: c.id,
        slug: c.slug,
        approvedId: c.approvedId,
        useAdminPreview: c.useAdminPreview,
        title: c.title,
        shortTitle: c.shortTitle,
        tags: c.tags,
        publishedAt: c.publishedAt,
        author: c.author,
        executiveSummary: c.executiveSummary,
        content: c.content,
        isNew: c.isNew
      };
    });
    const sorted = [...cards].sort((a, b) => a.title.localeCompare(b.title));
    setPublishedArticles(sorted);
    setIndexCache("articles", sorted);
  }, [apiArticles]);

  // Local-dev fallback: surface admin-approved articles from localStorage
  // when there's no cache and the API hasn't returned (server down, etc.).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isLocalDev || cachedArticles) return;
    try {
      const staticArticles = [];
      const newly = getAllNewlyAdded("article").filter(a => a.operation !== "deactivate");
      const merged = mergeNewlyAdded(staticArticles, newly, {
        markNew: true
      });
      const normalized = merged.map(record => {
        const idValue = typeof record.id === "string" ? record.id : typeof record.slug === "string" ? record.slug : record.approvalTimestamp;
        const id = typeof idValue === "string" ? idValue : String(idValue ?? "new");
        const slug = typeof record.slug === "string" && record.slug.length ? record.slug : undefined;
        const title = typeof record.title === "string" ? record.title : typeof record.shortTitle === "string" ? record.shortTitle : "Untitled";
        const rawTags = record.tags;
        const tags = Array.isArray(rawTags) ? rawTags.map(tag => String(tag)) : [];
        const rawAuthor = record.author;
        const author = rawAuthor && typeof rawAuthor === "object" && "name" in rawAuthor && typeof rawAuthor.name === "string" ? String(rawAuthor.name) : "Unknown";
        return {
          id,
          slug,
          approvedId: typeof record.id === "string" ? record.id : undefined,
          useAdminPreview: Boolean(record.useAdminPreview),
          title,
          shortTitle: typeof record.shortTitle === "string" ? record.shortTitle : undefined,
          tags,
          publishedAt: typeof record.approvalTimestamp === "number" ? record.approvalTimestamp : Date.now(),
          author,
          executiveSummary: typeof record.executiveSummary === "string" ? record.executiveSummary : undefined,
          content: typeof record.content === "string" ? record.content : undefined,
          isNew: Boolean(record.isNew)
        };
      });
      if (normalized.length) {
        setPublishedArticles([...normalized].sort((a, b) => a.title.localeCompare(b.title)));
      }
    } catch {
      // Don't overwrite with empty — leave current state
    }
  }, []);
  return {
    publishedArticles,
    articlesError
  };
}
const ArticlesPage = () => {
  const router = useRouter();
  const {
    settings: siteSettings
  } = useSiteSettings();
  useEffect(() => {
    if (!siteSettings.articlesVisible) void router.replace("/organizations");
  }, [siteSettings.articlesVisible, router]);
  const {
    isAuthenticated
  } = useAuth();
  const {
    publishedArticles,
    articlesError
  } = useArticlesIndexData();
  const {
    items: savedArticles
  } = useSavedItems("article");
  const hasSavedArticles = savedArticles.length > 0;
  const [prioritizeSaved, setPrioritizeSaved] = useState(false);

  // Set of saved article slugs/IDs for the sidenav "Saved" section and for the
  // optional prioritize-saved ordering.
  const savedArticleIdSet = useMemo(() => {
    const s = new Set();
    savedArticles.forEach(item => {
      const slug = String(item.resourceSlug ?? "").trim().toLowerCase();
      const id = String(item.resourceId ?? "").trim().toLowerCase();
      if (slug) s.add(slug);
      if (id) s.add(id);
    });
    return s;
  }, [savedArticles]);
  const [sidebarState, setSidebarState] = useState({
    isOpen: false,
    isPermanent: false,
    sidebarWidth: 320
  });
  const handleSidebarStateChange = useCallback(state => setSidebarState(state), []);
  const handleCreateClick = useCallback(() => {
    if (!isAuthenticated) {
      openAccessGateModal({
        reason: "login-required",
        returnUrl: "/articles/create"
      });
      return;
    }
    router.push("/articles/create");
  }, [isAuthenticated, router]);

  // Local create-draft "continue draft" card (mirrors the regulations index).
  const [draftArt, setDraftArt] = useState(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("articleDraft");
      setDraftArt(raw ? JSON.parse(raw) : null);
    } catch {
      setDraftArt(null);
    }
  }, []);
  const clearArtDraft = useCallback(() => {
    if (typeof window === "undefined") return;
    localStorage.removeItem("articleDraft");
    setDraftArt(null);
  }, []);

  // Saved articles sidenav section — dynamically shown only when the user has
  // saved articles, mirroring the regulations index.
  const savedArtNavItems = useMemo(() => {
    if (!savedArticles.length) return [];
    const seen = new Set();
    const items = [];
    savedArticles.forEach(item => {
      const key = String(item.resourceSlug ?? item.resourceId ?? "").trim();
      if (!key) return;
      const normalized = key.toLowerCase();
      if (seen.has(normalized)) return;
      seen.add(normalized);
      items.push({
        id: `saved-${normalized}`,
        label: item.resourceTitle || item.resourceSlug || item.resourceId || "Saved Article",
        href: `/articles/${key}`
      });
    });
    return items;
  }, [savedArticles]);

  // Build sidenav: "Saved" (when present) followed by "All Articles".
  const sideItems = useMemo(() => {
    const savedSections = savedArtNavItems.length ? [{
      title: "Saved",
      items: savedArtNavItems
    }] : [];
    return [...savedSections, {
      title: "All Articles",
      items: publishedArticles.map((article, index) => {
        const navId = getArticleNavKey(article, index);
        const routeSegment = getArticleRouteSegment(article);
        return {
          id: navId,
          label: getArticleLabel(article, index),
          ...(routeSegment ? {
            href: `/articles/${routeSegment}`
          } : {})
        };
      })
    }];
  }, [publishedArticles, savedArtNavItems]);

  // Dynamic filters: options are derived from the loaded articles, so a
  // select only ever offers values that will actually match something.
  const articleFilterSpecs = useMemo(() => [{
    name: "Tag",
    getValues: a => a.tags ?? []
  }, {
    name: "Author",
    getValues: a => [a.authorName ?? ""]
  }], []);
  const searchableArticleText = useCallback(a => [a.title, a.shortTitle ?? "", a.description ?? "", a.executiveSummary ?? "", (a.tags ?? []).join(" "), a.authorName ?? ""], []);
  const {
    searchQuery: articleSearch,
    setSearchQuery: setArticleSearch,
    viewMode: articleViewMode,
    setViewMode: setArticleViewMode,
    options: articleFilterOptions,
    selected: articleFilterValues,
    setSelectedAt: setArticleFilterAt,
    filtered: filteredArticles
  } = useIndexFiltering(publishedArticles, articleFilterSpecs, searchableArticleText, "list", INDEX_FILTER_MIN);

  // Optionally float saved articles to the top before progressive loading.
  const displayArticles = useMemo(() => {
    if (!prioritizeSaved || savedArticleIdSet.size === 0) return filteredArticles;
    const isSaved = article => {
      const slug = String(article.slug ?? "").trim().toLowerCase();
      const id = String(article.id ?? "").trim().toLowerCase();
      const approvedId = String(article.approvedId ?? "").trim().toLowerCase();
      return slug && savedArticleIdSet.has(slug) || id && savedArticleIdSet.has(id) || approvedId && savedArticleIdSet.has(approvedId);
    };
    const saved = [];
    const rest = [];
    filteredArticles.forEach(article => {
      (isSaved(article) ? saved : rest).push(article);
    });
    return [...saved, ...rest];
  }, [filteredArticles, prioritizeSaved, savedArticleIdSet]);

  // Progressive loading + relevance split (shared)
  const {
    visibleItems: visibleArticles,
    showSkeletons,
    skeletonCount
  } = useProgressiveLoading(displayArticles, 10, articlesError);
  const {
    relevant,
    remaining,
    showRelevanceSection,
    getHighlightedTags
  } = useRelevanceSplit(visibleArticles);
  const renderArticleCard = useCallback(article => {
    const routeSegment = getArticleRouteSegment(article);
    return /*#__PURE__*/_jsx(ArticleCard, {
      article: article,
      accentColor: ACCENT.primary,
      onClick: routeSegment ? () => router.push(`/articles/${routeSegment}`) : undefined,
      highlightedTags: getHighlightedTags(article.tags || []),
      listMode: articleViewMode === "list"
    });
  }, [router, getHighlightedTags, articleViewMode]);

  // One source of truth — shared with NavBar so the navbar's shift
  // agrees with whether the nav actually renders. Resolves false until
  // the setting is known, so it never flashes in and out.
  const showSideNav = useIndexSideNav("articles");
  const indexLayout = useIndexLayout("articles");
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(IndexPageHead, {
      title: "Articles Overview | Your Company",
      description: "Explore our curated articles and insights on compliance and product certification.",
      keywords: "articles, insights, guidance",
      canonicalUrl: "https://yourdomain.com/articles"
    }), /*#__PURE__*/_jsx(NavBar, {}), showSideNav && /*#__PURE__*/_jsx(Sidenav, {
      "aria-label": "Articles Sidebar",
      "data-select-target": "Side Navigation",
      title: "Articles Index",
      sections: sideItems,
      onSidebarStateChange: handleSidebarStateChange,
      permanentBreakpoint: "lg",
      linkColor: purple.primary,
      paletteVariant: "articles"
    }), /*#__PURE__*/_jsxs(IndexLayoutShell, {
      style: indexLayout.style,
      hasSideNav: showSideNav,
      accent: ACCENT,
      ariaLabel: "Articles List",
      children: [/*#__PURE__*/_jsx(IndexListHeader, {
        style: indexLayout.style,
        showCreate: true,
        ariaLabel: "Articles List Header",
        title: "Articles",
        createLabel: "Create Article",
        onCreate: handleCreateClick,
        accent: ACCENT,
        description: "Browse our collection of articles. Use the navigation panel to explore or click any article below to view details."
      }), /*#__PURE__*/_jsx(FilterBar, {
        accentColor: purple.primary,
        selectTarget: "Article Filters",
        searchSubjectLabel: "Articles",
        filterMin: INDEX_FILTER_MIN,
        itemCount: publishedArticles.length,
        search: true,
        searchPlaceholder: "Search articles...",
        searchValue: articleSearch,
        onSearchChange: setArticleSearch,
        searchScope: "articles",
        viewMode: articleViewMode,
        onViewModeChange: setArticleViewMode,
        showPrioritizeSaved: hasSavedArticles,
        prioritizeSaved: prioritizeSaved,
        onPrioritizeSavedChange: setPrioritizeSaved,
        filter1: {
          name: "Tag",
          pluralName: "Tags",
          options: articleFilterOptions[0] ?? [],
          value: articleFilterValues[0] ?? "",
          onChange: v => setArticleFilterAt(0, v)
        },
        filter2: {
          name: "Author",
          pluralName: "Authors",
          options: articleFilterOptions[1] ?? [],
          value: articleFilterValues[1] ?? "",
          onChange: v => setArticleFilterAt(1, v)
        }
      }), /*#__PURE__*/_jsx(IndexResultsGrid, {
        viewMode: articleViewMode,
        showRelevanceSection: showRelevanceSection,
        relevantItems: relevant,
        remainingItems: remaining,
        fallbackItems: visibleArticles,
        renderCard: renderArticleCard,
        getKey: getArticleNavKey,
        showSkeletons: showSkeletons,
        skeletonCount: skeletonCount,
        renderSkeleton: i => /*#__PURE__*/_jsx(ArticleCardSkeleton, {}, `skel-${i}`),
        leadingSlot: draftArt ? /*#__PURE__*/_jsx(CreateDraftCard, {
          draft: draftArt,
          untitledLabel: "Untitled Article",
          onOpen: handleCreateClick,
          onClear: clearArtDraft
        }) : null
      })]
    }), /*#__PURE__*/_jsx(SubscriptionBanner, {
      sidebarOffset: showSideNav && sidebarState.isPermanent ? sidebarState.sidebarWidth : 0
    }), /*#__PURE__*/_jsx(SectionFooter, {
      hideNewsletter: true,
      sidebarOffset: showSideNav && sidebarState.isPermanent ? sidebarState.sidebarWidth : 0
    })]
  });
};
export const getStaticProps = async () => {
  return {
    props: {
      featuredRegulations
    }
  };
};
export default ArticlesPage;